import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Building2, 
  Users, 
  Briefcase, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  XCircle,
  Search,
  Settings,
  RefreshCw,
  Loader2,
  Play,
  Eye,
  FileText,
  Download,
  BookOpen,
  HardHat,
  ClipboardList
} from 'lucide-react';
import { format, formatDistanceToNow, isPast } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editForm, setEditForm] = useState({
    plan: 'CORE',
    subscriptionStatus: 'ACTIVE',
    maxUsers: 14,
    maxJobs: 9999
  });
  const [saving, setSaving] = useState(false);
  
  // Training docs dialog
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Training Documents Content
  const trainingDocuments = [
    {
      id: 'foreman',
      title: 'Foreman Training Guide',
      description: 'Field operations guide for creating timesheets, tracking labor hours, and managing material purchase orders.',
      icon: HardHat,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      content: `# BSO SiteTrack - Foreman Training Guide

## Overview
As a Foreman, you are responsible for field operations including creating timesheets, tracking labor hours, and creating material purchase orders. This guide walks you through your daily workflows.

---

## Getting Started

### Logging In
1. Go to the BSO SiteTrack login page
2. Enter your email and password
3. Click **Sign In**
4. You'll see your personalized dashboard with your assigned jobs

---

## Daily Workflows

### 1. Creating a Timesheet

**When:** At the end of each work day or week

**Steps:**
1. Click **Timesheets** in the sidebar
2. Click the **+ New Timesheet** button
3. Select the **Date** for the timesheet
4. Click **Create Timesheet**
5. You'll be taken to the timesheet detail page to add labor entries

### 2. Adding Labor Entries to a Timesheet

**For each crew member who worked:**

1. On the timesheet detail page, click **+ Add Labor Entry**
2. Select the **Crew Member** from the dropdown
3. Select the **Job** they worked on
4. Select their **Classification** (e.g., Journeyman, Foreman)
5. Enter hours:
   - **ST Hours** = Straight time (first 8 hours)
   - **OT Hours** = Overtime (over 8 hours)
   - **DT Hours** = Double time (if applicable)
6. Click **Save**

**Multi-Job Entries:** If a crew member worked on multiple jobs, you can split their hours across different jobs in a single entry.

### 3. Submitting a Timesheet for Approval

**When:** After all labor entries are complete

1. Review all entries on the timesheet
2. Click **Submit for Approval**
3. The timesheet will be sent to the Office for review
4. Status changes from "Draft" to "Submitted"

**Note:** Once submitted, you cannot edit the timesheet. Contact Office if changes are needed.

---

## Creating Material Purchase Orders (POs)

### When to Create a PO
- Before purchasing materials for a job
- To track equipment rentals
- For subcontractor work

### Steps:
1. Click **Material POs** in the sidebar
2. Click **+ New PO**
3. Fill in the details:
   - **Job** - Select the job this PO is for
   - **Vendor** - Enter the vendor/supplier name
   - **Type** - Material, Equipment, Subcontractor, or Non-Billable
   - **Approx Amount** - Estimated cost
   - **Notes** - Any relevant details
4. Click **Create PO**
5. The PO number is auto-generated (e.g., MPO-0001)

**What happens next:** Office will update the PO when the invoice is received.

---

## Creating a New Job (If Enabled)

Some Foremen can create new jobs:

1. Click **Jobs** in the sidebar
2. Click **+ New Job**
3. Fill in:
   - **Customer** - Select from dropdown
   - **Job Type** - Time & Material or Lump Sum
   - **Job Number** - Auto-generated based on customer code
   - **Description** - Brief job description
   - **PO Value** - Total contract value
4. Click **Create Job**

---

## Viewing Your Assigned Jobs

1. Click **Jobs** in the sidebar
2. You'll see only jobs assigned to you
3. Click on any job card to see the **Job Dashboard** with:
   - Total charges (labor + materials)
   - PO utilization (how much of the budget is used)
   - Detailed breakdowns

---

## Quick Reference

| Task | Where | Button |
|------|-------|--------|
| Create Timesheet | Timesheets page | + New Timesheet |
| Add Labor Entry | Timesheet detail | + Add Labor Entry |
| Submit Timesheet | Timesheet detail | Submit for Approval |
| Create PO | Material POs page | + New PO |
| View Job Details | Jobs page | Click job card |

---

## Tips for Success

1. **Submit timesheets promptly** - Office needs them for billing
2. **Be accurate with hours** - This directly affects job costing
3. **Create POs before purchasing** - Helps track budgets
4. **Check PO amounts** - Watch for jobs nearing their budget limit
5. **Use notes fields** - Document any unusual circumstances

---

## Need Help?

Contact your Office administrator for:
- Password resets
- Access to additional jobs
- Timesheet corrections after submission
- Questions about job budgets`
    },
    {
      id: 'office',
      title: 'Office Administrator Training Guide',
      description: 'Complete guide for managing customers, approving timesheets, processing invoices, and overseeing job costing operations.',
      icon: ClipboardList,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      content: `# BSO SiteTrack - Office Administrator Training Guide

## Overview
As an Office Administrator, you have full access to BSO SiteTrack. Your responsibilities include managing customers, approving timesheets, processing invoices, and overseeing all job costing operations. This guide covers your complete workflow.

---

## Getting Started

### Logging In
1. Go to the BSO SiteTrack login page
2. Enter your email and password
3. Click **Sign In**
4. Your dashboard shows company-wide statistics and pending items

---

## Dashboard Overview

Your Office Dashboard displays:
- **Total Jobs** - Active projects count
- **Pending Timesheets** - Timesheets awaiting your approval
- **Total PO Value** - Sum of all job contract values
- **Crew Members** - Active workforce count

**Quick Actions:**
- Job Recap - Overall financial summary
- Invoices - Vendor invoice management
- Timesheets - Approval queue
- Jobs - Job management

---

## Daily Workflows

### 1. Approving Timesheets

**Steps:**
1. Click **Timesheets** in the sidebar
2. Look for timesheets with "Submitted" status
3. Click on a timesheet to review
4. Verify labor entries are correct:
   - Correct crew members
   - Accurate hours (ST/OT/DT)
   - Proper job assignments
   - Correct classifications
5. Click **Approve** to finalize

**If corrections needed:**
- Contact the Foreman to discuss
- You can move labor entries to different jobs if needed (with audit trail)

### 2. Processing Vendor Invoices

**When vendor invoices arrive:**

1. Click **Vendor Invoices** in the sidebar
2. Click **+ New Invoice**
3. Enter invoice details:
   - **Job** - Select the job
   - **Vendor** - Vendor name
   - **Invoice Number** - From the vendor's invoice
   - **Date** - Invoice date
   - **Type** - Material, Equipment, Subcontractor, or Non-Billable
   - **Amount** - Invoice total (before markup)
   - **Markup %** - Applied markup (default from settings or override)
4. Click **Save**

**The system calculates:**
- Total Charge = Amount + Markup

### 3. Updating Material PO Status

**When invoice arrives for a PO:**

1. Click **Material POs** in the sidebar
2. Find the PO (use filters by Job or Status)
3. Click **Edit**
4. Update:
   - **Status** → "Invoiced"
   - **Invoice Received Date**
   - **Actual Amount** (from the invoice)
5. Click **Save**

**When logged in accounting:**
- Change Status → "Logged"

---

## Customer Invoicing (Billing)

### Creating a Customer Invoice

1. Click **Customer Invoices** in the sidebar
2. Click **+ New Invoice**
3. Select the **Customer**
4. Select one or more **Jobs** to invoice
5. Click **Generate Line Items from Jobs**
   - This pulls labor charges and material costs automatically
6. Review and edit line items:
   - Add custom line items if needed
   - Remove items if not billing
   - Adjust descriptions
7. Set **Tax Rate** (if applicable)
8. Add **Notes** (optional)
9. Click **Create Invoice**

### Invoice Workflow

| Status | Meaning | Actions Available |
|--------|---------|-------------------|
| Draft | Just created | Edit, Delete, Mark as Sent |
| Sent | Invoice sent to customer | Mark as Paid, Mark as Overdue |
| Paid | Payment received | View only |
| Overdue | Past due date | Mark as Paid |

### Downloading PDF Invoice

1. Click **View** on any invoice
2. In the detail modal, click **Download PDF**
3. PDF includes:
   - Your company logo
   - Company address/phone/email
   - Invoice details and line items
   - Payment terms from customer settings

---

## Customer Management

### Adding a New Customer

1. Click **Customers** in the sidebar
2. Click **+ New Customer**
3. Fill in:
   - **Name** - Company name
   - **External ID** - Your internal reference (optional)
   - **Job Code** - 2-3 letter code for job numbering (e.g., "BH")
   - **PO Prefix** - Custom prefix for this customer's POs (optional)
   
   **Billing Settings:**
   - **Payment Terms** - Net 30, Net 45, Net 60, Due on Receipt, or Custom
   - **Billing Email** - For sending invoices
   - **Billing Address** - Appears on invoices
4. Click **Save**

### Job Code System

Job numbers are auto-generated: \`[YY][CustomerCode][Type][Seq]\`
- Example: \`26BH TM 001\` = 2026, Burns Harbor, Time & Material, sequence 1

---

## Job Management

### Creating a New Job

1. Click **Jobs** in the sidebar
2. Click **+ New Job**
3. Fill in:
   - **Customer** - Select customer
   - **Job Type** - Time & Material or Lump Sum
   - **Job Number** - Auto-generated (editable)
   - **Description** - Job name/description
   - **PO Value** - Contract amount
4. Optionally assign Foremen
5. Click **Create Job**

### Monitoring Job Costs

**Job Recap Dashboard:**
1. Click **Job Recap** in the sidebar
2. View all jobs with:
   - PO Value (budget)
   - Labor charges
   - Material charges
   - Pending PO amounts
   - Balance remaining

**Individual Job Dashboard:**
1. Click on any job card
2. See detailed breakdowns:
   - Labor by date and crew
   - Materials by vendor
   - Pending POs
3. Export data to Excel

---

## Settings Management

### Company Profile
1. Click **Settings** → **Company Profile** tab
2. Upload your company **logo**
3. Enter company **name, email, phone, address**
4. Click **Save**

*Logo appears in sidebar and on PDF invoices*

### Invoice Settings
1. Click **Settings** → **Invoice Settings** tab
2. Configure:
   - **Default Tax Rate** - Applied to new invoices
   - **Invoice Prefix** - e.g., "INV"
   - **Default Markup** - For vendor invoices
   - **Payment Instructions** - Appears on invoices
   - **Footer Notes** - Terms and conditions
   - **PO Prefix** - Default for Material POs
3. Click **Save**

### User Management
1. Click **Settings** → **User Management** tab
2. View all users and their roles

**Adding a New User:**
1. Click **Add User**
2. Enter:
   - Name
   - Email
   - Role (Foreman, Office, Owner)
   - Initial password
3. Click **Create User**

**Changing Roles or Deactivating:**
- Click **Edit** to change role
- Click **Deactivate** to disable login

---

## Audit Log

**View all system changes:**
1. Click **Audit Log** in the sidebar
2. See who changed what and when
3. Filter by:
   - Record type (Customer, Job, Invoice, etc.)
   - User
   - Date range
4. Click row to expand and see field-level changes

---

## Importing Data

### QuickBooks Invoice Import
1. Click **Import Data** in the sidebar
2. In the QuickBooks section, click **Select Excel File**
3. Upload your "Project Costs Detail by Vendor" export
4. Review parsed data in the preview table
5. Match projects to jobs using dropdown
6. Select invoices to import
7. Click **Import**

### Bulk Data Import
Download templates and import:
- Customers
- Crafts
- Classifications
- Jobs
- Crew Members
- Labor Entries
- Material POs

---

## Quick Reference

| Task | Location | Key Action |
|------|----------|------------|
| Approve timesheet | Timesheets | Click Approve |
| Create vendor invoice | Vendor Invoices | + New Invoice |
| Create customer invoice | Customer Invoices | + New Invoice → Generate Line Items |
| Download PDF | Customer Invoice detail | Download PDF button |
| Update PO status | Material POs | Edit → Change Status |
| Add user | Settings → Users | Add User |
| View audit trail | Audit Log | Filter and expand rows |

---

## Monthly Processes

1. **Review all pending timesheets** - Approve or follow up
2. **Process all vendor invoices** - Keep PO statuses current
3. **Generate customer invoices** - Bill for completed work
4. **Run Job Recap** - Check budget utilization
5. **Review audit log** - Verify data integrity

---

## Tips for Success

1. **Keep timesheets current** - Approve daily for accurate costing
2. **Match POs to invoices** - Helps track actual vs. estimated costs
3. **Set up customer billing info** - Ensures professional invoices
4. **Use the audit log** - Track who made changes and when
5. **Export reports regularly** - Keep records in Excel for accounting

---

## Need Help?

For technical support or feature requests, contact your system administrator.`
    }
  ];

  // Check if user is super admin
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchData();
    }
  }, [isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/companies`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Super Admin access required');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setEditForm({
      plan: company.plan || 'TRIAL',
      subscriptionStatus: company.subscriptionStatus || 'TRIAL',
      maxUsers: company.maxUsers || 14,
      maxJobs: company.maxJobs || 9999
    });
    setEditDialogOpen(true);
  };

  const handleSaveCompany = async () => {
    if (!editingCompany) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/api/admin/companies/${editingCompany.id}/plan`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to update company:', error);
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status, trialEndsAt) => {
    const isTrialExpired = status === 'TRIAL' && trialEndsAt && isPast(new Date(trialEndsAt));
    
    if (isTrialExpired) {
      return <Badge className="bg-red-100 text-red-700 border-red-200 rounded-sm">Trial Expired</Badge>;
    }
    
    const styles = {
      TRIAL: 'bg-blue-100 text-blue-700 border-blue-200',
      ACTIVE: 'bg-green-100 text-green-700 border-green-200',
      PAST_DUE: 'bg-amber-100 text-amber-700 border-amber-200',
      CANCELLED: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    
    return <Badge className={`${styles[status] || styles.TRIAL} rounded-sm`}>{status}</Badge>;
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          company.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || statusFilter === 'ALL' || company.subscriptionStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card className="border-red-200 bg-red-50 max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-600">This page is only accessible to Super Admins.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A3668]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Admin Dashboard</h1>
          <p className="text-slate-600 mt-1">Manage companies, subscriptions, and platform metrics</p>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          className="border-2 border-slate-200 rounded-sm"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold uppercase tracking-wider text-slate-500">Total Companies</p>
                  <p className="text-3xl font-bold text-[#1A3668]">{stats.totalCompanies}</p>
                </div>
                <Building2 className="w-8 h-8 text-slate-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold uppercase tracking-wider text-slate-500">On Trial</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.trialCompanies}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold uppercase tracking-wider text-slate-500">Active Paid</p>
                  <p className="text-3xl font-bold text-green-600">{stats.activeCompanies}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold uppercase tracking-wider text-slate-500">Total Users</p>
                  <p className="text-3xl font-bold text-[#1A3668]">{stats.totalUsers}</p>
                </div>
                <Users className="w-8 h-8 text-slate-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold uppercase tracking-wider text-slate-500">Total Jobs</p>
                  <p className="text-3xl font-bold text-[#1A3668]">{stats.totalJobs}</p>
                </div>
                <Briefcase className="w-8 h-8 text-slate-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revenue Estimate */}
      {stats && (
        <Card className="border-green-200 bg-green-50 shadow-sm rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-wider text-green-700">Estimated Monthly Revenue</p>
                <p className="text-3xl font-bold text-green-800">
                  ${(stats.activeCompanies * 75).toLocaleString()}/mo
                </p>
                <p className="text-sm text-green-600 mt-1">
                  Based on {stats.activeCompanies} active companies × $75/month (Core plan base)
                </p>
              </div>
              <DollarSign className="w-12 h-12 text-green-300" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Training Documents Section */}
      <Card className="border-slate-200 shadow-sm rounded-sm" data-testid="training-documents-section">
        <CardHeader>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#1A3668]" />
            <div>
              <CardTitle>Training Documents</CardTitle>
              <CardDescription>User guides and training materials for your clients</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {trainingDocuments.map((doc) => {
              const IconComponent = doc.icon;
              return (
                <div
                  key={doc.id}
                  className={`p-4 rounded-sm border-2 ${doc.borderColor} ${doc.bgColor} hover:shadow-md transition-shadow cursor-pointer`}
                  onClick={() => {
                    setSelectedDoc(doc);
                    setTrainingDialogOpen(true);
                  }}
                  data-testid={`training-doc-${doc.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-sm bg-white border ${doc.borderColor}`}>
                      <IconComponent className={`w-6 h-6 ${doc.color}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900">{doc.title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{doc.description}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className={`mt-3 rounded-sm ${doc.borderColor} hover:${doc.bgColor}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDoc(doc);
                          setTrainingDialogOpen(true);
                        }}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View Guide
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Companies Table */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle>All Companies</CardTitle>
              <CardDescription>Manage subscriptions and view company details</CardDescription>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 rounded-sm border-2"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 rounded-sm border-2">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="TRIAL">Trial</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="PAST_DUE">Past Due</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Users</TableHead>
                  <TableHead className="text-center">Jobs</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Trial Ends</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      No companies found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCompanies.map((company) => (
                    <TableRow key={company.id} data-testid={`company-row-${company.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-bold text-slate-900">{company.name}</p>
                          <p className="text-sm text-slate-500">{company.email || 'No email'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="rounded-sm">
                          {company.plan || 'TRIAL'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(company.subscriptionStatus, company.trialEndsAt)}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={company.userCount >= company.maxUsers ? 'text-red-600 font-bold' : ''}>
                          {company.userCount || 0}
                        </span>
                        <span className="text-slate-400">/{company.maxUsers || '∞'}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={company.jobCount >= company.maxJobs ? 'text-red-600 font-bold' : ''}>
                          {company.jobCount || 0}
                        </span>
                        <span className="text-slate-400">/{company.maxJobs >= 9999 ? '∞' : company.maxJobs}</span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {company.createdAt ? format(new Date(company.createdAt), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {company.trialEndsAt ? (
                          <span className={isPast(new Date(company.trialEndsAt)) ? 'text-red-600' : 'text-slate-600'}>
                            {formatDistanceToNow(new Date(company.trialEndsAt), { addSuffix: true })}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          onClick={() => handleEditCompany(company)}
                          variant="outline"
                          size="sm"
                          className="border-2 border-slate-200 hover:border-[#1A3668] rounded-sm"
                        >
                          <Settings className="w-3 h-3 mr-1" />
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Company Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#1A3668]" />
              Manage {editingCompany?.name}
            </DialogTitle>
            <DialogDescription>
              Update subscription plan and limits for this company
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Plan</Label>
              <Select value={editForm.plan} onValueChange={(v) => setEditForm({ ...editForm, plan: v })}>
                <SelectTrigger className="mt-2 rounded-sm border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TRIAL">Trial (Free)</SelectItem>
                  <SelectItem value="CORE">Core ($75/mo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Subscription Status</Label>
              <Select value={editForm.subscriptionStatus} onValueChange={(v) => setEditForm({ ...editForm, subscriptionStatus: v })}>
                <SelectTrigger className="mt-2 rounded-sm border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TRIAL">Trial</SelectItem>
                  <SelectItem value="ACTIVE">Active (Paid)</SelectItem>
                  <SelectItem value="PAST_DUE">Past Due</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Set to "Active" to grant full access without requiring payment
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Max Users</Label>
                <Input
                  type="number"
                  value={editForm.maxUsers}
                  onChange={(e) => setEditForm({ ...editForm, maxUsers: parseInt(e.target.value) || 14 })}
                  className="mt-2 rounded-sm border-2"
                />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Max Jobs</Label>
                <Input
                  type="number"
                  value={editForm.maxJobs}
                  onChange={(e) => setEditForm({ ...editForm, maxJobs: parseInt(e.target.value) || 9999 })}
                  className="mt-2 rounded-sm border-2"
                />
                <p className="text-xs text-slate-500 mt-1">9999 = Unlimited</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-slate-50 p-3 rounded-sm border border-slate-200">
              <p className="text-sm font-bold text-slate-700 mb-2">Quick Actions</p>
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditForm({ ...editForm, subscriptionStatus: 'ACTIVE', plan: 'CORE' })}
                  className="text-green-600 border-green-200 hover:bg-green-50 rounded-sm"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Activate (Free)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditForm({ ...editForm, maxUsers: 50, maxJobs: 9999 })}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50 rounded-sm"
                >
                  Expand Limits
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              onClick={handleSaveCompany}
              disabled={saving}
              className="bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Training Document Viewer Dialog */}
      <Dialog open={trainingDialogOpen} onOpenChange={setTrainingDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[80vh] rounded-sm" data-testid="training-doc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedDoc && (
                <>
                  <selectedDoc.icon className={`w-5 h-5 ${selectedDoc.color}`} />
                  {selectedDoc.title}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedDoc?.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[60vh] pr-4">
            {selectedDoc && (
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap font-mono text-xs bg-slate-50 p-4 rounded-sm border border-slate-200 overflow-x-auto">
                  {selectedDoc.content.split('\n').map((line, index) => {
                    // Simple markdown rendering
                    if (line.startsWith('# ')) {
                      return <h1 key={`line-${index}`} className="text-2xl font-bold text-[#1A3668] mt-4 mb-2 font-sans">{line.replace('# ', '')}</h1>;
                    } else if (line.startsWith('## ')) {
                      return <h2 key={`line-${index}`} className="text-xl font-bold text-slate-800 mt-4 mb-2 font-sans">{line.replace('## ', '')}</h2>;
                    } else if (line.startsWith('### ')) {
                      return <h3 key={`line-${index}`} className="text-lg font-bold text-slate-700 mt-3 mb-1 font-sans">{line.replace('### ', '')}</h3>;
                    } else if (line.startsWith('**') && line.endsWith('**')) {
                      return <p key={`line-${index}`} className="font-bold text-slate-800 mt-2 font-sans">{line.replace(/\*\*/g, '')}</p>;
                    } else if (line.startsWith('---')) {
                      return <hr key={`line-${index}`} className="my-4 border-slate-200" />;
                    } else if (line.startsWith('|')) {
                      return <p key={`line-${index}`} className="text-slate-600 font-mono text-xs">{line}</p>;
                    } else if (line.startsWith('- ') || line.startsWith('* ')) {
                      return <p key={`line-${index}`} className="text-slate-600 pl-4 font-sans text-sm">• {line.replace(/^[-*] /, '')}</p>;
                    } else if (line.match(/^\d+\./)) {
                      return <p key={`line-${index}`} className="text-slate-600 pl-4 font-sans text-sm">{line}</p>;
                    } else if (line.trim() === '') {
                      return <br key={`line-${index}`} />;
                    } else {
                      return <p key={`line-${index}`} className="text-slate-600 font-sans text-sm">{line}</p>;
                    }
                  })}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                if (selectedDoc) {
                  const blob = new Blob([selectedDoc.content], { type: 'text/markdown' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${selectedDoc.id}_training_guide.md`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success('Training guide downloaded');
                }
              }}
              className="rounded-sm"
              data-testid="download-training-doc"
            >
              <Download className="w-4 h-4 mr-2" />
              Download (.md)
            </Button>
            <Button
              onClick={() => setTrainingDialogOpen(false)}
              className="bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
